import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { APIProvider, Map, useMap, AdvancedMarker } from "@vis.gl/react-google-maps";
import type { TrackingReplay, TrackPoint } from "../../../../../../store/admin/trackingReplaysStore";
import type { ThemeClasses } from "../types";

interface TrackingReplayViewerModalProps {
  isOpen: boolean;
  replay: TrackingReplay;
  isDarkMode: boolean;
  themeClasses: ThemeClasses;
  onClose: () => void;
}

// Componente para dibujar la ruta en el mapa
const RoutePolyline = ({
  positions,
  currentIndex,
}: {
  positions: TrackPoint[];
  currentIndex: number;
}) => {
  const map = useMap();
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const traveledPolylineRef = useRef<google.maps.Polyline | null>(null);

  useEffect(() => {
    if (!map || positions.length === 0) return;

    // Limpiar polylines anteriores
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
    }
    if (traveledPolylineRef.current) {
      traveledPolylineRef.current.setMap(null);
    }

    const path = positions.map((pos) => ({
      lat: typeof pos.lat === "string" ? parseFloat(pos.lat) : pos.lat,
      lng: typeof pos.long === "string" ? parseFloat(pos.long) : pos.long,
    }));

    // Polyline completa (gris)
    polylineRef.current = new google.maps.Polyline({
      path,
      strokeColor: "#9ca3af",
      strokeOpacity: 0.6,
      strokeWeight: 4,
      map,
    });

    // Polyline recorrida (azul)
    const traveledPath = path.slice(0, currentIndex + 1);
    traveledPolylineRef.current = new google.maps.Polyline({
      path: traveledPath,
      strokeColor: "#3b82f6",
      strokeOpacity: 1,
      strokeWeight: 4,
      map,
    });

    // Ajustar bounds al inicio
    if (currentIndex === 0) {
      const bounds = new google.maps.LatLngBounds();
      path.forEach((p) => bounds.extend(p));
      map.fitBounds(bounds, { top: 50, right: 50, bottom: 50, left: 50 });
    }

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
      }
      if (traveledPolylineRef.current) {
        traveledPolylineRef.current.setMap(null);
      }
    };
  }, [map, positions, currentIndex]);

  return null;
};

// Componente del marcador del vehículo con tooltip
const VehicleMarker = ({
  position,
  pointData,
  isDarkMode,
}: {
  position: { lat: number; lng: number };
  pointData: TrackPoint;
  isDarkMode: boolean;
}) => {
  const [showTooltip, setShowTooltip] = useState(true);

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleDateString("es-ES", {
      day: "2-digit",
      month: "short",
      year: "numeric",
    });
  };

  const formatTime = (dateStr?: string) => {
    if (!dateStr) return "-";
    const date = new Date(dateStr);
    return date.toLocaleTimeString("es-ES", {
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  };

  const getSpeed = () => {
    if (pointData.event?.speed !== undefined) {
      return `${Math.round(pointData.event.speed)} km/h`;
    }
    return "-";
  };

  return (
    <AdvancedMarker position={position}>
      <div className="relative">
        {/* Tooltip */}
        {showTooltip && (
          <div
            className={`absolute bottom-full left-1/2 -translate-x-1/2 mb-2 px-3 py-2 rounded-lg shadow-lg text-xs whitespace-nowrap z-50 ${
              isDarkMode
                ? "bg-dark-card border border-dark-border text-white"
                : "bg-white border border-gray-200 text-gray-900"
            }`}
          >
            <div className="flex flex-col gap-1">
              <div className="flex items-center gap-2">
                <i className="ri-calendar-line text-primary-500"></i>
                <span>{formatDate(pointData.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-time-line text-primary-500"></i>
                <span>{formatTime(pointData.date)}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="ri-speed-line text-primary-500"></i>
                <span>{getSpeed()}</span>
              </div>
            </div>
            {/* Flecha del tooltip */}
            <div
              className={`absolute top-full left-1/2 -translate-x-1/2 w-0 h-0 border-l-[6px] border-r-[6px] border-t-[6px] border-l-transparent border-r-transparent ${
                isDarkMode ? "border-t-dark-card" : "border-t-white"
              }`}
            />
          </div>
        )}

        {/* Marcador del vehículo */}
        <div
          onClick={() => setShowTooltip(!showTooltip)}
          className="w-10 h-10 bg-primary-500 rounded-full flex items-center justify-center shadow-lg cursor-pointer hover:scale-110 transition-transform border-2 border-white"
        >
          <i className="ri-car-fill text-white text-lg"></i>
        </div>
      </div>
    </AdvancedMarker>
  );
};

// Componente principal
export function TrackingReplayViewerModal({
  isOpen,
  replay,
  isDarkMode,
  themeClasses,
  onClose,
}: TrackingReplayViewerModalProps) {
  const { t } = useTranslation("adminSections");

  // Estados de reproducción
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [playbackSpeed, setPlaybackSpeed] = useState(1); // 1x, 2x, 4x
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  // Posiciones válidas
  const validPositions = useMemo(() => {
    return replay.positions.filter((pos) => {
      const lat = typeof pos.lat === "string" ? parseFloat(pos.lat) : pos.lat;
      const lng = typeof pos.long === "string" ? parseFloat(pos.long) : pos.long;
      return !isNaN(lat) && !isNaN(lng) && 
             (lat !== 0 || lng !== 0) && // Permitir coordenadas donde al menos una no sea 0
             Math.abs(lat) <= 90 && Math.abs(lng) <= 180; // Validar rangos geográficos
    });
  }, [replay.positions]);

  // Posición actual
  const currentPosition = useMemo(() => {
    if (validPositions.length === 0) return null;
    const pos = validPositions[currentIndex];
    return {
      lat: typeof pos.lat === "string" ? parseFloat(pos.lat) : pos.lat,
      lng: typeof pos.long === "string" ? parseFloat(pos.long) : pos.long,
    };
  }, [validPositions, currentIndex]);

  // Punto actual
  const currentPoint = useMemo(() => {
    return validPositions[currentIndex] || null;
  }, [validPositions, currentIndex]);

  // Centro inicial del mapa
  const initialCenter = useMemo(() => {
    if (validPositions.length === 0) return { lat: 19.4326, lng: -99.1332 };
    const pos = validPositions[0];
    return {
      lat: typeof pos.lat === "string" ? parseFloat(pos.lat) : pos.lat,
      lng: typeof pos.long === "string" ? parseFloat(pos.long) : pos.long,
    };
  }, [validPositions]);

  // Efecto para la reproducción automática
  useEffect(() => {
    if (isPlaying && validPositions.length > 0) {
      const interval = (5000 / playbackSpeed); // 5 segundos base, ajustado por velocidad
      intervalRef.current = setInterval(() => {
        setCurrentIndex((prev) => {
          if (prev >= validPositions.length - 1) {
            setIsPlaying(false);
            return prev;
          }
          return prev + 1;
        });
      }, interval);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [isPlaying, playbackSpeed, validPositions.length]);

  // Handlers
  const handlePlay = useCallback(() => {
    if (currentIndex >= validPositions.length - 1) {
      setCurrentIndex(0);
    }
    setIsPlaying(true);
  }, [currentIndex, validPositions.length]);

  const handlePause = useCallback(() => {
    setIsPlaying(false);
  }, []);

  const handleStop = useCallback(() => {
    setIsPlaying(false);
    setCurrentIndex(0);
  }, []);

  const handlePrevious = useCallback(() => {
    setCurrentIndex((prev) => Math.max(0, prev - 1));
  }, []);

  const handleNext = useCallback(() => {
    setCurrentIndex((prev) => Math.min(validPositions.length - 1, prev + 1));
  }, [validPositions.length]);

  const handleSliderChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentIndex(parseInt(e.target.value, 10));
  }, []);

  const handleSpeedChange = useCallback(() => {
    setPlaybackSpeed((prev) => {
      if (prev === 1) return 2;
      if (prev === 2) return 4;
      return 1;
    });
  }, []);

  // Formatear duración
  const formatDuration = (startDate: string, endDate: string): string => {
    const start = new Date(startDate).getTime();
    const end = new Date(endDate).getTime();
    const durationMs = end - start;
    const hours = Math.floor(durationMs / (1000 * 60 * 60));
    const minutes = Math.floor((durationMs % (1000 * 60 * 60)) / (1000 * 60));

    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  // Limpiar al cerrar
  useEffect(() => {
    if (!isOpen) {
      setIsPlaying(false);
      setCurrentIndex(0);
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    }
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal */}
      <div
        className={`relative w-[95vw] sm:w-[80vw] h-[90vh] rounded-xl overflow-hidden flex flex-col ${
          isDarkMode ? "bg-dark-surface" : "bg-white"
        }`}
      >
        {/* Header */}
        <div
          className={`flex items-center justify-between gap-3 px-4 sm:px-6 py-4 border-b ${
            isDarkMode ? "border-gray-700" : "border-gray-200"
          }`}
        >
          <div className="flex flex-wrap items-center gap-2 sm:gap-4 min-w-0">
            <h2
              className={`text-base sm:text-xl font-semibold truncate max-w-full ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {replay.name}
            </h2>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isDarkMode ? "bg-primary-500/20 text-primary-400" : "bg-primary-100 text-primary-700"
              }`}
            >
              {validPositions.length} {t("trackingReplays.points")}
            </span>
            <span
              className={`px-2 py-1 rounded-full text-xs font-medium ${
                isDarkMode ? "bg-gray-700 text-gray-300" : "bg-gray-100 text-gray-700"
              }`}
            >
              {formatDuration(replay.start_date, replay.end_date)}
            </span>
          </div>
          <button
            onClick={onClose}
            className={`p-2 rounded-lg transition-colors ${
              isDarkMode
                ? "hover:bg-gray-700 text-gray-400 hover:text-white"
                : "hover:bg-gray-100 text-gray-600 hover:text-gray-900"
            }`}
          >
            <i className="ri-close-line text-2xl" />
          </button>
        </div>

        {/* Contenido principal - Mapa */}
        <div className="flex-1 relative">
          {validPositions.length === 0 ? (
            <div className="flex items-center justify-center h-full">
              <p className={themeClasses.textSecondary}>
                {t("trackingReplays.noPositions")}
              </p>
            </div>
          ) : (
            <APIProvider
              apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}
              libraries={["places"]}
            >
              <Map
                defaultZoom={14}
                defaultCenter={initialCenter}
                gestureHandling="greedy"
                mapId="TRACKING_REPLAY_MAP"
                style={{
                  width: "100%",
                  height: "100%",
                }}
              >
                <RoutePolyline
                  positions={validPositions}
                  currentIndex={currentIndex}
                />
                {currentPosition && currentPoint && (
                  <VehicleMarker
                    position={currentPosition}
                    pointData={currentPoint}
                    isDarkMode={isDarkMode}
                  />
                )}
              </Map>
            </APIProvider>
          )}

          {/* Info overlay */}
          <div
            className={`absolute top-4 left-4 px-4 py-3 rounded-lg shadow-lg ${
              isDarkMode
                ? "bg-dark-card/90 border border-dark-border"
                : "bg-white/90 border border-gray-200"
            }`}
          >
            <div className={`text-sm font-medium ${themeClasses.text}`}>
              {t("trackingReplays.position")} {currentIndex + 1} / {validPositions.length}
            </div>
          </div>
        </div>

        {/* Controles de reproducción */}
        <div
          className={`px-4 sm:px-6 py-4 border-t ${
            isDarkMode ? "border-gray-700 bg-dark-card" : "border-gray-200 bg-gray-50"
          }`}
        >
          {/* Slider de progreso */}
          <div className="mb-4">
            <input
              type="range"
              min={0}
              max={validPositions.length - 1}
              value={currentIndex}
              onChange={handleSliderChange}
              className="w-full h-2 bg-gray-300 rounded-lg appearance-none cursor-pointer accent-primary-500"
            />
          </div>

          {/* Botones de control */}
          <div className="flex flex-wrap items-center justify-center gap-2 sm:gap-4">
            {/* Botón de velocidad */}
            <button
              onClick={handleSpeedChange}
              className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                isDarkMode
                  ? "bg-gray-700 text-gray-300 hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              {playbackSpeed}x
            </button>

            {/* Anterior */}
            <button
              aria-label={t("trackingReplays.player.previous")}
              onClick={handlePrevious}
              disabled={currentIndex === 0}
              className={`p-3 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <i className="ri-skip-back-fill text-xl"></i>
            </button>

            {/* Play/Pause */}
            {isPlaying ? (
              <button
                aria-label={t("trackingReplays.player.pause")}
                onClick={handlePause}
                className="p-4 rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors"
              >
                <i className="ri-pause-fill text-2xl"></i>
              </button>
            ) : (
              <button
                aria-label={t("trackingReplays.player.play")}
                onClick={handlePlay}
                className="p-4 rounded-full bg-primary-500 text-white hover:bg-primary-600 transition-colors"
              >
                <i className="ri-play-fill text-2xl"></i>
              </button>
            )}

            {/* Siguiente */}
            <button
              onClick={handleNext}
              disabled={currentIndex >= validPositions.length - 1}
              className={`p-3 rounded-full transition-colors disabled:opacity-50 disabled:cursor-not-allowed ${
                isDarkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <i className="ri-skip-forward-fill text-xl"></i>
            </button>

            {/* Stop */}
            <button
              aria-label={t("trackingReplays.player.stop")}
              onClick={handleStop}
              className={`p-3 rounded-full transition-colors ${
                isDarkMode
                  ? "bg-gray-700 text-white hover:bg-gray-600"
                  : "bg-gray-200 text-gray-700 hover:bg-gray-300"
              }`}
            >
              <i className="ri-stop-fill text-xl"></i>
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TrackingReplayViewerModal;
